import { setDoc, deleteField } from "firebase/firestore";
import dayjs from "dayjs";
import { JobAction } from "~/interface/Job";

export default defineNuxtPlugin(() => {
  const bootstrapStore = useBootstrapStore();
  const shopStore = usePosShopStore();
  const jobV1Store = usePosJobsStore();
  const receiptsStore = usePosReceiptsStore();

  let unwatchTables: (() => void) | undefined;

  const toNumber = (value: unknown): number => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const computeTotals = () => {
    const tables = (jobV1Store.tables || {}) as Record<string, any>;
    const receipts = receiptsStore.receipts || [];

    let tableItemsTotal = 0;
    let tableGrandTotal = 0;
    let receiptsGrandTotal = 0;
    let tablesCount = 0;

    for (const table of Object.values(tables)) {
      const items = Array.isArray(table?.items) ? table.items : [];
      tableItemsTotal += items.reduce((sum: number, item: any) => {
        const itemTotal =
          item?.discountedPrice ??
          item?.extendedPrice ??
          toNumber(item?.price) * toNumber(item?.quantity);
        return sum + toNumber(itemTotal);
      }, 0);
      tableGrandTotal += toNumber(table?.billing?.grandTotal);
      tablesCount += 1;
    }

    for (const receipt of receipts) {
      const receiptAmount =
        receipt?.GrandTotal ?? receipt?.billing?.grandTotal ?? receipt?.grandTotal;
      receiptsGrandTotal += toNumber(receiptAmount);
    }

    return {
      tableItemsTotal,
      tableGrandTotal,
      receiptsGrandTotal,
      tablesCount,
      computedAt: new Date().toISOString(),
    };
  };

  // ---------------------------------------------------------------------------
  // Back-office usage analytics — where do ADD jobs come from?
  //
  //   job.source === 'm'  → mobile (every phone path funnels through
  //                         AdapterMobileV1: mobile-v1, mobile-v2, job gateway)
  //   job.source === 't'  → terminal (CartPanel.onSaveFromTerminal)
  //   no source           → cashier (CartPanel.onSaveToTable)
  //
  // The device id is NOT on the job card — it only exists inside the mobile
  // order payload (job.json). Parsing it is the expensive part, so memoise per
  // jobId: the tables watcher fires on every order and the chain only grows.
  const deviceIdCache = new Map<string, string>();
  // A cashier terminal can stay open for weeks; cap the memo so it can never
  // become a slow leak. Oldest entry out — Map preserves insertion order.
  const DEVICE_ID_CACHE_MAX = 5000;

  // Bucket for mobile ADDs whose payload carries no usable device id (older
  // orders, an unparsable json, a client that never sent the field). Without it
  // those orders vanish from `deviceIds` and the map no longer reconciles with
  // analytic_add_counts.mobile — which is exactly the number we want to trust.
  const UNKNOWN_DEVICE_ID = "unknown";

  // Firestore field names: no . # $ / [ ], not empty, and `__x__` is reserved.
  // Device ids are short hex strings in practice — this only exists so ONE
  // malformed id can never reject the whole document write.
  const safeKey = (value: string) => {
    const cleaned = String(value).replace(/[.#$/\[\]]/g, "_").trim();
    if (!cleaned) return UNKNOWN_DEVICE_ID;
    return /^__.*__$/.test(cleaned) ? `id_${cleaned}` : cleaned;
  };

  const getMobileDeviceId = (job: any): string => {
    const cacheKey = String(job?.jobId ?? "");
    if (cacheKey && deviceIdCache.has(cacheKey)) return deviceIdCache.get(cacheKey)!;

    let deviceId = "";
    try {
      deviceId = String(JSON.parse(job?.json || "{}")?.deviceId ?? "").trim();
    } catch {
      deviceId = "";
    }

    if (cacheKey) {
      if (deviceIdCache.size >= DEVICE_ID_CACHE_MAX) {
        const oldest = deviceIdCache.keys().next().value;
        if (oldest !== undefined) deviceIdCache.delete(oldest);
      }
      deviceIdCache.set(cacheKey, deviceId);
    }
    return deviceId;
  };

  const computeJobAnalytics = () => {
    const jobs = jobV1Store.getJobsArray() || [];
    const addCounts = { cashier: 0, terminal: 0, mobile: 0 };
    // A Map, not a plain object: an id like `constructor` or `toString` would
    // otherwise read the value off Object.prototype, and `(fn || 0) + 1` writes
    // a STRING into the counter.
    const counts = new Map<string, number>();

    for (const job of jobs) {
      if (job?.type !== JobAction.ADD) continue;

      const source = String(job?.source || "");
      if (source === "m") {
        addCounts.mobile += 1;
        // Always bucket — a missing id lands in `unknown` so that
        // sum(deviceIds) === addCounts.mobile always holds.
        const key = safeKey(getMobileDeviceId(job));
        counts.set(key, (counts.get(key) || 0) + 1);
      } else if (source === "t") {
        addCounts.terminal += 1;
      } else {
        addCounts.cashier += 1;
      }
    }

    return {
      jobsCount: jobs.length,
      addCounts,
      deviceIds: Object.fromEntries(counts) as Record<string, number>,
    };
  };

  // setDoc({ merge: true }) merges nested maps, so a device that stops ordering
  // (or a fresh business day) would leave its old count behind forever. Remember
  // what we wrote last time and explicitly deleteField() the keys that dropped.
  let lastDeviceIdKeys: string[] = [];
  // Dedupe on the serialized payload (cloud.mobile-v2.plugin's lastSummaryJson
  // pattern): the tables watcher fires on every recompute — up to 5/s behind
  // the 200ms jobchain debounce — and most ticks produce a byte-identical
  // document. Each skipped setDoc also skips the snapshot echo it bounces to
  // every subscribed device and the owner-mobile debounced refetch.
  let lastLivePayloadJson = "";
  const buildDeviceIdsPayload = (deviceIds: Record<string, number>) => {
    const payload: Record<string, any> = { ...deviceIds };
    for (const key of lastDeviceIdKeys) {
      // hasOwnProperty, not `in` — `'toString' in {}` is true.
      if (!Object.prototype.hasOwnProperty.call(deviceIds, key)) {
        payload[key] = deleteField();
      }
    }
    return payload;
  };

  const onTablesChanged = async () => {
    if (!bootstrapStore.loaded || shopStore.runMode !== "cashier") {
      return;
    }

    // This is a background telemetry write — it must NEVER surface as an error to
    // the person selling. The watcher calls us with `void onTablesChanged()`, so
    // anything thrown here (offline, denied by rules, an empty storeId making
    // doc() throw, an undefined value Firestore refuses) would become an
    // unhandled rejection on every single table change. Swallow and log.
    try {
      const totals = computeTotals();
      const analytics = computeJobAnalytics();

      // doc(db, 'customers', '') THROWS ("empty path segment") — it does not
      // return null. Bail while the store id is still unresolved instead of
      // logging a warning on every table change during bootstrap.
      const firestore = useFirestore();
      if (!firestore.storeId.value) return;
      const rootRef = firestore.getRootDataRef();

      const bills = usePosReceiptsStore().data?.length || 0;
      const storeName = useSettingsStore().getStoreName();
      const deviceIdsPayload = buildDeviceIdsPayload(analytics.deviceIds);

      const payload = {
          // never write `undefined` — Firestore rejects the whole document
          name: storeName || "",
          ...(analytics.jobsCount > 0 ? { status: 'opened' } : {}),
          live: {
            paid: totals.receiptsGrandTotal,
            pending: Object.values(jobV1Store.tables || {}).reduce((sum: number, table: any) => sum + toNumber(table?.billing?.grandTotal), 0),
            tables: totals.tablesCount,
            bills: bills,
            // guests: totals.tablesCount,
            businessDate: jobV1Store.getBusinessDate() || dayjs().format("YYYY-MM-DD"),
            lastUpdated: jobV1Store.getLastUpdate() || dayjs().format("YYYY-MM-DD HH:mm:ss"),

            // number of objects in the drop chain (jobchain)
            jobschain: analytics.jobsCount,
            // how the ADD jobs got in — cashier screen / terminal / mobile phones
            analytic_add_counts: analytics.addCounts,
            // per-phone ADD counts (mobile orders only) → how many phones are in
            // use. sum(deviceIds) === analytic_add_counts.mobile, ids that the
            // order payload never carried are bucketed under `unknown`.
            deviceIds: deviceIdsPayload,
          },
      };

      // deleteField() sentinels stringify consistently, so equal payloads
      // compare equal both ways.
      const payloadJson = JSON.stringify(payload);
      if (payloadJson === lastLivePayloadJson) return;

      await setDoc(rootRef, payload, { merge: true });
      lastLivePayloadJson = payloadJson;

      // Only remember the keys we actually landed — a failed write must not make
      // the next one think those keys already exist (or need deleting).
      lastDeviceIdKeys = Object.keys(analytics.deviceIds);

      console.log("[jobs.live] totals computed:", totals, analytics);
    } catch (error) {
      console.warn("[jobs.live] live update skipped:", error);
    }
  };

  const shouldWatchLive = computed(() => {
    return bootstrapStore.loaded && shopStore.runMode === "cashier";
  });

  watch(
    shouldWatchLive,
    (enabled, previousEnabled) => {
      if (enabled === previousEnabled) {
        return;
      }

      if (enabled) {
        if (unwatchTables) return;

        unwatchTables = watch(
          () => jobV1Store.tables,
          () => {
            void onTablesChanged();
          },
          { deep: false, immediate: true }
        );
        return;
      }

      if (unwatchTables) {
        unwatchTables();
        unwatchTables = undefined;
      }
    },
    { immediate: true }
  );
});
